import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { Share2, Instagram, Facebook, Hash } from 'lucide-react';
import Button from '../components/UI/Button';
import apiService from '../services/api';
import { useAuth } from '../contexts/AuthContext';

interface SocialFormData {
  instagramLink: string;
  facebookLink: string;
  hashtag: string;
}

const SocialSettings: React.FC = () => {
  const { currentFlavourId } = useAuth();
  const [existingRecord, setExistingRecord] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SocialFormData>({
    defaultValues: { instagramLink: '', facebookLink: '', hashtag: '' },
  });

  const fetchSettings = async () => {
    if (!currentFlavourId) {
      setExistingRecord(null);
      reset({ instagramLink: '', facebookLink: '', hashtag: '' });
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const res = await apiService.getSocialSettings(currentFlavourId);
      const data = res?.data ?? null;
      setExistingRecord(data);
      if (data) {
        reset({
          instagramLink: data.instagramLink ?? '',
          facebookLink: data.facebookLink ?? '',
          hashtag: data.hashtag ?? '',
        });
      } else {
        reset({ instagramLink: '', facebookLink: '', hashtag: '' });
      }
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load social settings.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFlavourId]);

  const isValidUrl = (value: string) => {
    if (!value) return true; // optional
    try {
      new URL(value);
      return true;
    } catch {
      return 'Please enter a valid URL.';
    }
  };

  const onSubmit = async (formData: SocialFormData) => {
    if (!currentFlavourId) return;
    const payload = {
      instagramLink: formData.instagramLink || undefined,
      facebookLink: formData.facebookLink || undefined,
      hashtag: formData.hashtag || undefined,
    };
    setIsSubmitting(true);
    try {
      if (existingRecord) {
        await apiService.updateSocialSettings(currentFlavourId, payload);
        toast.success('Social settings updated successfully.');
      } else {
        await apiService.createSocialSettings(currentFlavourId, payload);
        toast.success('Social settings created successfully.');
      }
      await fetchSettings();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save social settings.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!currentFlavourId || !existingRecord) return;
    if (!window.confirm('Are you sure you want to delete the social settings?')) return;
    setIsDeleting(true);
    try {
      await apiService.deleteSocialSettings(currentFlavourId);
      toast.success('Social settings deleted successfully.');
      setExistingRecord(null);
      reset({ instagramLink: '', facebookLink: '', hashtag: '' });
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete social settings.');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
          <Share2 className="w-5 h-5 text-black" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Social Media Settings</h1>
          <p className="text-sm text-gray-500">
            Manage social media links and hashtag for this flavour.
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Status badge */}
          <div className="mb-6">
            {existingRecord ? (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                Record exists — use Update to save changes
              </span>
            ) : (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                No record yet — fill the form and click Create
              </span>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Instagram Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5">
                  <Instagram className="w-4 h-4 text-pink-500" />
                  Instagram Link
                </span>
              </label>
              <input
                type="text"
                placeholder="https://instagram.com/yourpage"
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition ${
                  errors.instagramLink ? 'border-red-400' : 'border-gray-300'
                }`}
                {...register('instagramLink', { validate: isValidUrl })}
              />
              {errors.instagramLink && (
                <p className="mt-1 text-xs text-red-500">{errors.instagramLink.message}</p>
              )}
            </div>

            {/* Facebook Link */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5">
                  <Facebook className="w-4 h-4 text-blue-600" />
                  Facebook Link
                </span>
              </label>
              <input
                type="text"
                placeholder="https://facebook.com/yourpage"
                className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition ${
                  errors.facebookLink ? 'border-red-400' : 'border-gray-300'
                }`}
                {...register('facebookLink', { validate: isValidUrl })}
              />
              {errors.facebookLink && (
                <p className="mt-1 text-xs text-red-500">{errors.facebookLink.message}</p>
              )}
            </div>

            {/* Hashtag */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <span className="flex items-center gap-1.5">
                  <Hash className="w-4 h-4 text-gray-500" />
                  Hashtag
                </span>
              </label>
              <input
                type="text"
                placeholder="#flavourtour"
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 transition"
                {...register('hashtag')}
              />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between pt-2">
              <Button
                type="submit"
                variant="primary"
                loading={isSubmitting}
                disabled={isSubmitting || !currentFlavourId}
                title={!currentFlavourId ? 'Please create an app first' : ''}
              >
                {existingRecord ? 'Update' : 'Create'}
              </Button>

              {existingRecord && (
                <Button
                  type="button"
                  variant="danger"
                  loading={isDeleting}
                  disabled={isDeleting}
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              )}
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default SocialSettings;
